export const DEFAULT_SHARED_ACCESS = { organization: "View", users: [] };
// opref refuses Team grants (`Sharing.GranteeType.NotSupported`): they are neither mapped nor revoked.
const isUserGrant = (g) => g.granteeType === "User" && g.granteeId !== null;
/**
 * A plain copy: a value a component emits can carry nested reactive proxies, which
 * `structuredClone` rejects, and requests built after an await must not see later edits.
 */
export function cloneSharing({ destination, access }) {
    return {
        destination,
        access: {
            organization: access.organization,
            users: access.users.map((u) => ({ id: u.id, level: u.level })),
        },
    };
}
export function isPrivate({ destination, access }) {
    return destination === "personal" || (access.organization === null && access.users.length === 0);
}
export function grantsToSharing(grants, fallbackName) {
    const organization = grants.find((g) => g.granteeType === "Organization")?.permission ?? null;
    const userGrants = grants.filter(isUserGrant);
    return {
        sharing: {
            destination: organization === null && userGrants.length === 0 ? "personal" : "shared",
            access: {
                organization,
                users: userGrants.map((g) => ({ id: g.granteeId, level: g.permission })),
            },
        },
        knownUsers: userGrants.map((g) => ({
            id: g.granteeId,
            name: g.granteeDisplayName ?? fallbackName,
            ...(g.granteeStatus === "Disabled" ? { inactive: true } : {}),
        })),
    };
}
export function sharingToRequests(sharing) {
    if (sharing.destination === "personal")
        return [];
    const { organization, users } = sharing.access;
    return [
        ...(organization === null ? [] : [{ granteeType: "Organization", permission: organization }]),
        ...users.map((u) => ({ granteeType: "User", granteeId: u.id, permission: u.level })),
    ];
}
export function diffSharing(baseline, next) {
    const baselineOrg = baseline.find((g) => g.granteeType === "Organization")?.permission ?? null;
    const baselineUsers = new Map(baseline.filter(isUserGrant).map((g) => [g.granteeId, g.permission]));
    const wantedOrg = next.destination === "shared" ? next.access.organization : null;
    const wantedUsers = next.destination === "shared" ? next.access.users : [];
    const requests = [];
    if (wantedOrg !== baselineOrg) {
        requests.push({ granteeType: "Organization", permission: wantedOrg });
    }
    for (const u of wantedUsers) {
        if (baselineUsers.get(u.id) !== u.level) {
            requests.push({ granteeType: "User", granteeId: u.id, permission: u.level });
        }
    }
    const wantedIds = new Set(wantedUsers.map((u) => u.id));
    for (const id of baselineUsers.keys()) {
        if (!wantedIds.has(id)) {
            requests.push({ granteeType: "User", granteeId: id, permission: null });
        }
    }
    return requests;
}
